import { Bot, Brain, Crown, Star, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL, apiFetch } from '../../config/api'

interface Agent {
  agent_id: number
  agent_name: string
  expertise: string
  level: number
  total_xp: number
  role: string
  user_type: string
  status: string
}

const ROLE_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  lead:        { label: 'Lead',        color: '#f59e0b', icon: <Crown size={12} /> },
  expert:      { label: 'Expert',      color: '#6366f1', icon: <Star size={12} /> },
  contributor: { label: 'Contributor', color: '#10b981', icon: <Bot size={12} /> },
  worker:      { label: 'Worker',      color: '#3b82f6', icon: <Brain size={12} /> },
  mentor:      { label: 'Mentor',      color: '#ec4899', icon: <Star size={12} /> },
}

function getRoleConfig(role: string) {
  return ROLE_CONFIG[role] || { label: role, color: 'var(--secondary)', icon: <Bot size={12} /> }
}

function AgentAvatar({ name, role }: { name: string; role: string }) {
  const cfg = getRoleConfig(role)
  return (
    <div style={{
      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
      background: `linear-gradient(135deg, ${cfg.color}33, ${cfg.color}11)`,
      border: `2px solid ${cfg.color}55`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: cfg.color, fontWeight: 700, fontSize: 16
    }}>
      {name.charAt(0).toUpperCase()}
    </div>
  )
}

export default function ResearchActiveAgents({ researchId }: { researchId: number }) {
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/research/projects/${researchId}/active-agents`)
        const data = await res.json() as { agents: Agent[] }
        setAgents(data.agents || [])
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [researchId])

  if (loading) {return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>}

  if (agents.length === 0) {return (
    <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-secondary)' }}>
      <Users size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
      <p style={{ margin: 0 }}>No active agents yet</p>
      <p style={{ fontSize: 13, marginTop: 6, opacity: 0.7 }}>Agents are assigned automatically when research starts</p>
    </div>
  )}

  // Role'e göre grupla
  const grouped: Record<string, Agent[]> = {}
  for (const a of agents) {
    const r = a.role || 'contributor'
    if (!grouped[r]) {grouped[r] = []}
    grouped[r].push(a)
  }

  const roleOrder = ['lead', 'mentor', 'expert', 'contributor', 'worker']

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <Users size={18} style={{ color: 'var(--primary-color)' }} />
        <span style={{ fontWeight: 600, fontSize: 16 }}>Active Agents</span>
        <span style={{
          background: 'var(--border-color)', color: 'var(--text-secondary)',
          borderRadius: 20, padding: '1px 8px', fontSize: 12
        }}>{agents.length}</span>
      </div>

      {roleOrder.filter(r => grouped[r]?.length).map(role => {
        const cfg = getRoleConfig(role)
        return (
          <div key={role} style={{ marginBottom: 24 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginBottom: 12, color: cfg.color, fontSize: 12, fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.06em'
            }}>
              {cfg.icon} {cfg.label}s ({grouped[role].length})
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 12
            }}>
              {grouped[role].map(agent => (
                <div key={agent.agent_id} style={{
                  background: 'var(--card-bg)', border: '1px solid var(--border-color)',
                  borderRadius: 12, padding: '14px 16px',
                  display: 'flex', alignItems: 'center', gap: 12
                }}>
                  <AgentAvatar name={agent.agent_name} role={agent.role} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 600, fontSize: 14, color: 'var(--text-primary)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>
                      {agent.agent_name}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {agent.expertise || 'General'}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                      <span style={{
                        fontSize: 11, background: cfg.color + '18', color: cfg.color,
                        padding: '1px 7px', borderRadius: 10, fontWeight: 600
                      }}>
                        Lv {agent.level || 1}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {(agent.total_xp || 0).toLocaleString()} XP
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
